import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import eyeOpenIcon from "../../../images/eye-open.svg";
import eyeCloseIcon from "../../../images/eye-closed.svg";

export default function Login() {
  const [activeTab, setActiveTab] = useState("login"); // "login" or "register"
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agree, setAgree] = useState(false);
  const [showTnC, setShowTnC] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Verification code states
  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [codeSending, setCodeSending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Login step: "credentials" or "verify"
  const [loginStep, setLoginStep] = useState("credentials");

  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState("email"); // "email", "verify", "reset"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Register form fields
  const [registerData, setRegisterData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    company: "",
    password: "",
    confirmPassword: "",
    verificationCode: "",
  });
  const [registerStep, setRegisterStep] = useState("form"); // "form" or "verify"
  const [registerCodeSent, setRegisterCodeSent] = useState(false);

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
    setSuccess(null);
    setCodeSent(false);
    setVerificationCode("");
    setLoginStep("credentials");
    setRegisterStep("form");
    setRegisterCodeSent(false);
  };

  // Check if email exists (for registration)
  const checkEmailExists = useCallback(async (emailToCheck) => {
    try {
      const res = await fetch("/api/auth/check-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailToCheck }),
      });
      const data = await res.json();
      return data.exists;
    } catch {
      return false;
    }
  }, []);

  // Send verification code (password required for login type)
  const sendCode = async (targetEmail, type = "login", pwd = null) => {
    if (codeSending) {
      return false; // Already sending
    }
    if (countdown > 0) {
      setError(`Please wait ${countdown} seconds before requesting a new code`);
      return false;
    }

    setCodeSending(true);
    setError(null);

    try {
      const body = { email: targetEmail, type };
      if (pwd) body.password = pwd;

      const res = await fetch("/api/auth/send-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setCodeSent(true);
        setCountdown(10); // 60 second countdown
        setSuccess("Verification code sent to your email");
        setTimeout(() => setSuccess(null), 3000);
        return true;
      } else {
        setError(data.message || "Failed to send verification code");
        return false;
      }
    } catch {
      setError("Failed to send verification code. Please try again.");
      return false;
    } finally {
      setCodeSending(false);
    }
  };

  // Login handler - now with verification code
  const handleSignIn = async () => {
    if (loading) return;
    if (!agree) {
      setError("Please agree to the terms and conditions");
      return;
    }
    setError(null);

    if (loginStep === "credentials") {
      // Step 1: Enter email and password, then send code
      if (!email) {
        setError("Please enter your email address.");
        return;
      }

      if (!password) {
        setError("Please enter your password.");
        return;
      }

      try {
        setLoading(true);
        // Verify credentials first, then send verification code
        const codeSentSuccess = await sendCode(email.trim(), "login", password.trim());
        if (codeSentSuccess) {
          setLoginStep("verify");
        }
      } catch {
        setError("Connection error. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      // Step 2: Verify code and complete login with password
      if (!verificationCode || verificationCode.length !== 6) {
        setError("Please enter the 6-digit verification code.");
        return;
      }

      try {
        setLoading(true);
        const res = await fetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            email: email.trim(),
            password: password.trim(),
            code: verificationCode.trim(),
          }),
        });

        const data = await res.json().catch(() => ({ success: false, message: "Server error" }));

        if (res.ok && data.success) {
          try {
            localStorage.setItem("userId", String(data.id));
            if (data.token) localStorage.setItem("token", String(data.token));
            localStorage.setItem("userCode", String(data.code || email));
            if (data.username) localStorage.setItem("username", String(data.username));
            if (data.roleId) localStorage.setItem("roleId", String(data.roleId));
            if (data.roleName) localStorage.setItem("roleName", String(data.roleName));
            if (data.roleType) localStorage.setItem("roleType", String(data.roleType));
            if (data.permissions) localStorage.setItem("permissions", JSON.stringify(data.permissions));
          } catch (_) {}

          // Superadmin or users with dashboard:read go to /dashboard, others go to /profile
          const isSuperadmin = data.roleType === "SUPERADMIN";
          const hasDashboard = isSuperadmin || (data.permissions || []).includes("dashboard:read");
          navigate(hasDashboard ? "/dashboard" : "/profile", { replace: true });
        } else if (res.status === 403 || res.status === 423) {
          // Account disabled (403) or locked (423)
          setError("Account is locked. Please contact Powow Operations for assistance.");
        } else {
          setError(data.message || "Invalid credentials or verification code");
        }
      } catch {
        setError("Connection error. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Register handler - now with verification code
  const handleRegister = async () => {
    if (loading) return;
    if (!agree) {
      setError("Please agree to the terms and conditions");
      return;
    }
    setError(null);
    setSuccess(null);

    const { email: regEmail, firstName, lastName, company, password: regPassword, confirmPassword, verificationCode: regCode } = registerData;

    if (registerStep === "form") {
      // Step 1: Validate form and send code
      if (!regEmail || !firstName || !lastName || !regPassword) {
        setError("Please fill in all required fields.");
        return;
      }

      if (regPassword !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }

      if (regPassword.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }

      // Check if email already exists
      setLoading(true);
      const exists = await checkEmailExists(regEmail.trim());

      if (exists) {
        setError("An account with this email already exists. Please sign in instead.");
        setLoading(false);
        return;
      }

      // Send verification code
      const codeSentSuccess = await sendCode(regEmail.trim(), "register");
      setLoading(false);

      if (codeSentSuccess) {
        setRegisterStep("verify");
        setRegisterCodeSent(true);
      }
    } else {
      // Step 2: Verify code and complete registration
      if (!regCode || regCode.length !== 6) {
        setError("Please enter the 6-digit verification code.");
        return;
      }

      try {
        setLoading(true);
        const res = await fetch("/api/auth/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            email: regEmail.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            company: company.trim(),
            password: regPassword,
            code: regCode.trim(),
          }),
        });

        const data = await res.json().catch(() => ({ success: false, message: "Server error" }));

        if (res.ok && data.success) {
          const isCompanyPending = data.status === "COMPANY_PENDING";
          setSuccess(
            isCompanyPending
              ? "Registration successful! Your company is pending approval. You can sign in but will have limited access until approved."
              : "Registration successful! Please sign in."
          );
          setRegisterData({
            email: "",
            firstName: "",
            lastName: "",
            company: "",
            password: "",
            confirmPassword: "",
            verificationCode: "",
          });
          setRegisterStep("form");
          setRegisterCodeSent(false);
          setTimeout(() => setActiveTab("login"), 2000);
        } else {
          setError(data.message || "Registration failed. Please try again.");
        }
      } catch {
        setError("Connection error. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Forgot password handler
  const handleForgotPassword = async () => {
    if (loading) return;
    setError(null);
    setSuccess(null);

    if (forgotStep === "email") {
      // Step 1: Send reset code
      if (!forgotEmail) {
        setError("Please enter your email address.");
        return;
      }

      setLoading(true);
      try {
        const res = await fetch("/api/auth/forgot-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: forgotEmail.trim() }),
        });

        const data = await res.json();
        console.log('API response:', { ok: res.ok, data });

        if (res.ok && data.success) {
          console.log('Setting forgotStep to verify');
          setForgotStep("verify");
          setCountdown(10);
          setSuccess("Reset code sent to your email");
          setTimeout(() => setSuccess(null), 3000);
        } else {
          console.log('API failed:', data);
          setError(data.message || "Failed to send reset code");
        }
      } catch {
        setError("Connection error. Please try again.");
      } finally {
        setLoading(false);
      }
    } else if (forgotStep === "verify") {
      // Step 2: Verify code
      if (!forgotCode || forgotCode.length !== 6) {
        setError("Please enter the 6-digit verification code.");
        return;
      }
      setForgotStep("reset");
      setError(null);
    } else {
      // Step 3: Reset password
      if (!newPassword || newPassword.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }

      if (newPassword !== confirmNewPassword) {
        setError("Passwords do not match.");
        return;
      }

      setLoading(true);
      try {
        const res = await fetch("/api/auth/reset-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            email: forgotEmail.trim(),
            code: forgotCode.trim(),
            newPassword: newPassword,
          }),
        });

        const data = await res.json();

        if (res.ok && data.success) {
          // Clear countdown
          setCountdown(0);

          // Store user data for auto-login
          try {
            localStorage.setItem("userId", String(data.id));
            if (data.token) localStorage.setItem("token", String(data.token));
            localStorage.setItem("userCode", String(data.code || forgotEmail));
            if (data.username) localStorage.setItem("username", String(data.username));
            if (data.roleId) localStorage.setItem("roleId", String(data.roleId));
            if (data.roleName) localStorage.setItem("roleName", String(data.roleName));
            if (data.roleType) localStorage.setItem("roleType", String(data.roleType));
            if (data.permissions) localStorage.setItem("permissions", JSON.stringify(data.permissions));
          } catch (_) {}

          // Navigate to dashboard (auto-login)
          navigate("/dashboard", { replace: true });
        } else {
          setError(data.message || "Failed to reset password");
        }
      } catch {
        setError("Connection error. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === "Enter") {
      action();
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotStep("email");
    setForgotEmail("");
    setForgotCode("");
    setNewPassword("");
    setConfirmNewPassword("");
    setError(null);
    setSuccess(null);
  };

  // Render verification code input
  const renderCodeInput = (value, onChange, onResend, targetEmail, type) => (
    <div className="verification-section">
      <div className="form-field">
        <label htmlFor="verificationCode" className="label">
          Verification Code
        </label>
        <div className="code-input-wrapper">
          <input
            id="verificationCode"
            type="text"
            placeholder="Enter 6-digit code"
            className="input code-input"
            maxLength={6}
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
            autoComplete="one-time-code"
          />
          <button
            type="button"
            className="resend-btn"
            disabled={countdown > 0 || codeSending}
            onClick={() => sendCode(targetEmail, type)}
          >
            {countdown > 0 ? `${countdown}s` : codeSending ? "Sending..." : "Resend"}
          </button>
        </div>
        <p className="code-hint">Enter the 6-digit code sent to your email</p>
      </div>
    </div>
  );

  return (
    <div className="login-page">
      {/* Left Section - Branding */}
      <section className="login-left" aria-hidden="true">
        <div className="login-left-content">
          <img src="/powow-white.png" alt="Powow" className="login-hero-logo" />
          <h2 className="login-hero-title">VPP Platform</h2>
          <p className="login-hero-subtitle">
            Manage your virtual power plant assets with real-time insights and powerful analytics.
          </p>
        </div>
      </section>

      {/* Right Section - Form */}
      <section className="login-right" aria-label="Authentication">
        <div className="login-card" role="form">
          {/* Logo for mobile */}
          <div className="login-brand-mobile">
            <img src="/powow.png" alt="Powow" className="brand-img-mobile" />
          </div>

          {/* Tabs */}
          <div className="auth-tabs">
            <button
              className={`auth-tab ${activeTab === "login" ? "active" : ""}`}
              onClick={() => handleTabChange("login")}
              type="button"
            >
              Sign In
            </button>
            <button
              className={`auth-tab ${activeTab === "register" ? "active" : ""}`}
              onClick={() => handleTabChange("register")}
              type="button"
            >
              Register
            </button>
            <div
              className="auth-tab-indicator"
              style={{ transform: activeTab === "register" ? "translateX(100%)" : "translateX(0)" }}
            />
          </div>

          {/* Login Form */}
          {activeTab === "login" && (
            <div className="auth-form">
              <h1 className="auth-title">Welcome back</h1>
              <p className="auth-subtitle">
                {loginStep === "credentials"
                  ? "Enter your credentials to access your account"
                  : "Enter the verification code sent to your email"}
              </p>

              {loginStep === "credentials" ? (
                <>
                  <div className="form-field">
                    <label htmlFor="email" className="label">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      className="input"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, handleSignIn)}
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="password" className="label">
                      Password
                    </label>
                    <div className="password-wrapper">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        className="input password-input"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, handleSignIn)}
                      />
                      <button
                        type="button"
                        className="toggle-visibility"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <img
                          src={showPassword ? eyeCloseIcon : eyeOpenIcon}
                          alt={showPassword ? "Hide" : "Show"}
                        />
                      </button>
                    </div>
                  </div>

                  <div className="forgot-password-link">
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot password?
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="email-display">
                    <span className="email-label">Sending code to:</span>
                    <span className="email-value">{email}</span>
                  </div>
                  {renderCodeInput(
                    verificationCode,
                    setVerificationCode,
                    () => sendCode(email.trim(), "login", password.trim()),
                    email.trim(),
                    "login"
                  )}
                </>
              )}

              {error && (
                <div className="auth-error" role="alert">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1C4.1 1 1 4.1 1 8s3.1 7 7 7 7-3.1 7-7-3.1-7-7-7zm-.5 3h1v5h-1V4zm.5 8c-.4 0-.8-.3-.8-.8s.3-.8.8-.8.8.3.8.8-.4.8-.8.8z" />
                  </svg>
                  {error}
                </div>
              )}

              {success && (
                <div className="auth-success" role="status">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1C4.1 1 1 4.1 1 8s3.1 7 7 7 7-3.1 7-7-3.1-7-7-7zm3.7 5.3l-4 4c-.2.2-.4.3-.7.3s-.5-.1-.7-.3l-2-2c-.4-.4-.4-1 0-1.4s1-.4 1.4 0L7 8.2l3.3-3.3c.4-.4 1-.4 1.4 0s.4 1 0 1.4z" />
                  </svg>
                  {success}
                </div>
              )}

              <div className="form-field checkbox-field">
                <label className="checkbox-label">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                  <span className="checkbox-custom" />
                  <span>I agree to Powow's <a href="#" onClick={(e) => { e.preventDefault(); setShowTnC(true); }} style={{ color: "#1890ff", textDecoration: "underline" }}>terms and conditions</a></span>
                </label>
              </div>

              <button
                className="btn-primary"
                disabled={!agree || loading}
                onClick={handleSignIn}
                type="button"
              >
                {loading ? (
                  <span className="btn-loading">
                    <span className="spinner" />
                    {loginStep === "credentials" ? "Verifying..." : "Signing in..."}
                  </span>
                ) : loginStep === "credentials" ? (
                  "Continue"
                ) : (
                  "Sign in"
                )}
              </button>

              <p className="auth-footer">
                Don't have an account?{" "}
                <button type="button" className="link-btn" onClick={() => handleTabChange("register")}>
                  Create one
                </button>
              </p>
            </div>
          )}

          {/* Register Form */}
          {activeTab === "register" && (
            <div className="auth-form">
              <h1 className="auth-title">Create account</h1>
              <p className="auth-subtitle">
                {registerStep === "form"
                  ? "Join the Powow partner network"
                  : "Enter the verification code sent to your email"}
              </p>

              {registerStep === "form" ? (
                <>
                  <div className="form-row">
                    <div className="form-field half">
                      <label htmlFor="firstName" className="label">
                        First Name *
                      </label>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        placeholder="John"
                        className="input"
                        value={registerData.firstName}
                        onChange={(e) => setRegisterData({ ...registerData, firstName: e.target.value })}
                      />
                    </div>
                    <div className="form-field half">
                      <label htmlFor="lastName" className="label">
                        Last Name *
                      </label>
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        placeholder="Smith"
                        className="input"
                        value={registerData.lastName}
                        onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="regEmail" className="label">
                      Email *
                    </label>
                    <input
                      id="regEmail"
                      name="regEmail"
                      type="email"
                      placeholder="you@company.com"
                      className="input"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="company" className="label">
                      Company
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      placeholder="Your company name"
                      className="input"
                      value={registerData.company}
                      onChange={(e) => setRegisterData({ ...registerData, company: e.target.value })}
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="regPassword" className="label">
                      Password *
                    </label>
                    <div className="password-wrapper">
                      <input
                        id="regPassword"
                        name="regPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Min. 8 characters"
                        className="input password-input"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      />
                      <button
                        type="button"
                        className="toggle-visibility"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <img
                          src={showPassword ? eyeCloseIcon : eyeOpenIcon}
                          alt={showPassword ? "Hide" : "Show"}
                        />
                      </button>
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="confirmPassword" className="label">
                      Confirm Password *
                    </label>
                    <div className="password-wrapper">
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        className="input password-input"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        onKeyPress={(e) => handleKeyPress(e, handleRegister)}
                      />
                      <button
                        type="button"
                        className="toggle-visibility"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        <img
                          src={showConfirmPassword ? eyeCloseIcon : eyeOpenIcon}
                          alt={showConfirmPassword ? "Hide" : "Show"}
                        />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="email-display">
                    <span className="email-label">Sending code to:</span>
                    <span className="email-value">{registerData.email}</span>
                    <button
                      type="button"
                      className="change-email-btn"
                      onClick={() => {
                        setRegisterStep("form");
                        setRegisterData({ ...registerData, verificationCode: "" });
                        setRegisterCodeSent(false);
                      }}
                    >
                      Change
                    </button>
                  </div>
                  {renderCodeInput(
                    registerData.verificationCode,
                    (val) => setRegisterData({ ...registerData, verificationCode: val }),
                    () => sendCode(registerData.email.trim(), "register"),
                    registerData.email.trim(),
                    "register"
                  )}
                </>
              )}

              {error && (
                <div className="auth-error" role="alert">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1C4.1 1 1 4.1 1 8s3.1 7 7 7 7-3.1 7-7-3.1-7-7-7zm-.5 3h1v5h-1V4zm.5 8c-.4 0-.8-.3-.8-.8s.3-.8.8-.8.8.3.8.8-.4.8-.8.8z" />
                  </svg>
                  {error}
                </div>
              )}

              {success && (
                <div className="auth-success" role="status">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1C4.1 1 1 4.1 1 8s3.1 7 7 7 7-3.1 7-7-3.1-7-7-7zm3.7 5.3l-4 4c-.2.2-.4.3-.7.3s-.5-.1-.7-.3l-2-2c-.4-.4-.4-1 0-1.4s1-.4 1.4 0L7 8.2l3.3-3.3c.4-.4 1-.4 1.4 0s.4 1 0 1.4z" />
                  </svg>
                  {success}
                </div>
              )}

              <div className="form-field checkbox-field">
                <label className="checkbox-label">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                  <span className="checkbox-custom" />
                  <span>I agree to Powow's <a href="#" onClick={(e) => { e.preventDefault(); setShowTnC(true); }} style={{ color: "#1890ff", textDecoration: "underline" }}>terms and conditions</a></span>
                </label>
              </div>

              <button
                className="btn-primary"
                disabled={!agree || loading}
                onClick={handleRegister}
                type="button"
              >
                {loading ? (
                  <span className="btn-loading">
                    <span className="spinner" />
                    {registerStep === "form" ? "Checking..." : "Creating account..."}
                  </span>
                ) : registerStep === "form" ? (
                  "Continue"
                ) : (
                  "Create account"
                )}
              </button>

              <p className="auth-footer">
                Already have an account?{" "}
                <button type="button" className="link-btn" onClick={() => handleTabChange("login")}>
                  Sign in
                </button>
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Forgot Password Modal - Outside login-right for proper centering */}
      {showForgotPassword && (
        <div className="modal-overlay" onClick={closeForgotPassword}>
          <div className="modal-content forgot-password-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeForgotPassword}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M15.8 4.2c-.4-.4-1-.4-1.4 0L10 8.6 5.6 4.2c-.4-.4-1-.4-1.4 0s-.4 1 0 1.4L8.6 10l-4.4 4.4c-.4.4-.4 1 0 1.4.2.2.4.3.7.3s.5-.1.7-.3l4.4-4.4 4.4 4.4c.2.2.5.3.7.3s.5-.1.7-.3c.4-.4.4-1 0-1.4L11.4 10l4.4-4.4c.4-.4.4-1 0-1.4z" />
              </svg>
            </button>

            <h2 className="modal-title">Reset Password</h2>
            <p className="modal-subtitle">
              {forgotStep === "email" && "Enter your email address to receive a reset code"}
              {forgotStep === "verify" && "Enter the verification code sent to your email"}
              {forgotStep === "reset" && "Create a new password for your account"}
            </p>

            {forgotStep === "email" && (
              <div className="form-field">
                <label htmlFor="forgotEmail" className="label">
                  Email
                </label>
                <input
                  id="forgotEmail"
                  type="email"
                  placeholder="you@example.com"
                  className="input"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handleForgotPassword)}
                />
              </div>
            )}

            {forgotStep === "verify" && (
              <>
                <div className="email-display">
                  <span className="email-label">Sending code to:</span>
                  <span className="email-value">{forgotEmail}</span>
                </div>
                <div className="form-field">
                  <label htmlFor="forgotCode" className="label">
                    Verification Code
                  </label>
                  <div className="code-input-wrapper">
                    <input
                      id="forgotCode"
                      type="text"
                      placeholder="Enter 6-digit code"
                      className="input code-input"
                      maxLength={6}
                      value={forgotCode}
                      onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ""))}
                      onKeyPress={(e) => handleKeyPress(e, handleForgotPassword)}
                    />
                    <button
                      type="button"
                      className="resend-btn"
                      disabled={countdown > 0 || loading}
                      onClick={async () => {
                        setLoading(true);
                        await fetch("/api/auth/forgot-password", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ email: forgotEmail.trim() }),
                        });
                        setCountdown(10);
                        setLoading(false);
                      }}
                    >
                      {countdown > 0 ? `${countdown}s` : "Resend"}
                    </button>
                  </div>
                </div>
              </>
            )}

            {forgotStep === "reset" && (
              <>
                <div className="form-field">
                  <label htmlFor="newPassword" className="label">
                    New Password
                  </label>
                  <div className="password-wrapper">
                    <input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Min. 8 characters"
                      className="input password-input"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="toggle-visibility"
                      onClick={() => setShowNewPassword((v) => !v)}
                    >
                      <img
                        src={showNewPassword ? eyeCloseIcon : eyeOpenIcon}
                        alt={showNewPassword ? "Hide" : "Show"}
                      />
                    </button>
                  </div>
                </div>
                <div className="form-field">
                  <label htmlFor="confirmNewPassword" className="label">
                    Confirm New Password
                  </label>
                  <div className="password-wrapper">
                    <input
                      id="confirmNewPassword"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Confirm your new password"
                      className="input password-input"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, handleForgotPassword)}
                    />
                  </div>
                </div>
              </>
            )}

            {error && (
              <div className="auth-error" role="alert">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1C4.1 1 1 4.1 1 8s3.1 7 7 7 7-3.1 7-7-3.1-7-7-7zm-.5 3h1v5h-1V4zm.5 8c-.4 0-.8-.3-.8-.8s.3-.8.8-.8.8.3.8.8-.4.8-.8.8z" />
                </svg>
                {error}
              </div>
            )}

            {success && (
              <div className="auth-success" role="status">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1C4.1 1 1 4.1 1 8s3.1 7 7 7 7-3.1 7-7-3.1-7-7-7zm3.7 5.3l-4 4c-.2.2-.4.3-.7.3s-.5-.1-.7-.3l-2-2c-.4-.4-.4-1 0-1.4s1-.4 1.4 0L7 8.2l3.3-3.3c.4-.4 1-.4 1.4 0s.4 1 0 1.4z" />
                </svg>
                {success}
              </div>
            )}

            <button
              className="btn-primary"
              disabled={loading}
              onClick={handleForgotPassword}
              type="button"
            >
              {loading ? (
                <span className="btn-loading">
                  <span className="spinner" />
                  {forgotStep === "email" ? "Sending..." : forgotStep === "verify" ? "Verifying..." : "Resetting..."}
                </span>
              ) : forgotStep === "email" ? (
                "Send Reset Code"
              ) : forgotStep === "verify" ? (
                "Verify Code"
              ) : (
                "Reset Password"
              )}
            </button>

            {forgotStep !== "email" && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  if (forgotStep === "verify") {
                    setForgotStep("email");
                  } else {
                    setForgotStep("verify");
                  }
                  setError(null);
                }}
              >
                Back
              </button>
            )}
          </div>
        </div>
      )}
      {showTnC && (
        <div className="tnc-modal-overlay" onClick={() => setShowTnC(false)}>
          <div className="tnc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tnc-modal-header">
              <h2>Terms and Conditions</h2>
              <button className="tnc-close" onClick={() => setShowTnC(false)}>&times;</button>
            </div>
            <div className="tnc-modal-body">
              <h3>POWOW VPP PLATFORM TERMS AND CONDITIONS</h3>
              <p>These Terms and Conditions ("Terms") govern your access to and use of the Powow Virtual Power Plant Platform (the "Platform") operated by Powow VPP Pty Ltd ("Powow", "we", "us", or "our"). These Terms apply to all business partners, integrators, aggregators, and service providers ("Partner", "you", or "your") who access or use the Platform.</p>
              <p>By registering an account, accessing, or using the Platform, you agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use the Platform.</p>

              <h4>1. Definitions</h4>
              <p>"Platform" means the Powow VPP software platform including the web portal, APIs, device integration services, data processing infrastructure, dispatch systems, and any related services made available by Powow.</p>
              <p>"Partner" means any organisation or business entity that registers for or accesses the Platform.</p>
              <p>"DER" or "Device" means any distributed energy resource connected to the Platform including, without limitation, battery energy storage systems, solar inverters, EV chargers, controllable loads, and other grid-connected equipment.</p>
              <p>"Services" means the services provided through the Platform including device monitoring, data processing, optimisation, dispatch control, settlement processing, and other related VPP services.</p>

              <h4>2. Eligibility</h4>
              <p>The Platform is intended solely for business use. By accessing or using the Platform you represent and warrant that you are a legally registered business entity and have the authority to bind your organisation to these Terms. You also represent that your use of the Platform complies with all applicable laws, regulations, and industry requirements.</p>
              <p>Powow may approve or reject any Partner registration at its sole discretion.</p>

              <h4>3. Account Registration</h4>
              <p>To access the Platform, you must create a Partner account. You agree to provide accurate and complete information during registration and to keep such information up to date.</p>
              <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must ensure that only authorised personnel access the Platform through your account.</p>
              <p>Powow is not responsible for any loss or damage arising from unauthorised access resulting from your failure to safeguard your account credentials.</p>

              <h4>4. Platform Licence</h4>
              <p>Subject to these Terms, Powow grants you a limited, non-exclusive, non-transferable, revocable licence to access and use the Platform for your internal business purposes.</p>
              <p>You may use the Platform solely for the purpose of integrating, monitoring, managing, or operating distributed energy resources and related services.</p>
              <p>You must not copy, reproduce, modify, distribute, sell, sublicense, reverse engineer, or attempt to derive the source code of the Platform except to the extent permitted by law.</p>

              <h4>5. Device Integration</h4>
              <p>Partners may connect DER devices to the Platform through supported integration methods including approved APIs, device gateways, or manufacturer integrations.</p>
              <p>You are responsible for ensuring that all connected devices are properly installed, comply with applicable technical standards, and operate safely and in accordance with regulatory requirements.</p>
              <p>Powow reserves the right to restrict, suspend, or disconnect any device that presents a safety, security, operational, or compliance risk.</p>

              <h4>6. Dispatch and Operational Control</h4>
              <p>Where DER assets participate in VPP services, the Platform may issue operational commands including, without limitation, charge or discharge instructions, export limits, event activations, or other control signals.</p>
              <p>You acknowledge and agree that (a) dispatch and optimisation may be automated and algorithm-driven; (b) operational outcomes may vary and depend on factors outside Powow's control; and (c) Powow does not guarantee any specific operational or financial outcome.</p>

              <h4>7. Data Collection and Use</h4>
              <p>The Platform may collect, process, store, and use data including device telemetry, performance data, configuration data, and related operational information.</p>
              <p>Powow may use such data to operate and improve the Platform, provide the Services, perform analytics and optimisation, support settlement and billing, and meet compliance or reporting obligations.</p>

              <h4>8. API Access and Security</h4>
              <p>If Powow provides API access, you must comply with Powow's API documentation and security requirements.</p>
              <p>You must not (a) share API keys outside your organisation; (b) attempt to circumvent rate limits; or (c) use the APIs in a manner that degrades Platform performance or security.</p>

              <h4>9. Third-Party Services</h4>
              <p>The Platform may interact with or rely on third-party services, systems, or data sources. Powow does not control third-party services and does not warrant their availability, performance, or continuity.</p>

              <h4>10. Fees and Payment</h4>
              <p>Platform access or certain services may be subject to fees. Fees will be agreed in a separate order form, statement of work, or commercial agreement; or published by Powow with reasonable notice.</p>

              <h4>11. Confidentiality</h4>
              <p>Each party may receive confidential information of the other party. The receiving party must keep confidential information confidential, use it only to perform or receive the Services, and disclose it only to personnel who need to know.</p>

              <h4>12. Intellectual Property</h4>
              <p>The Platform and all related intellectual property rights are and remain the property of Powow or its licensors. Except for the limited licence granted in Section 4, no rights are granted to you.</p>

              <h4>13. Acceptable Use</h4>
              <p>You must not (a) use the Platform for any unlawful purpose; (b) interfere with or disrupt the Platform; (c) introduce malware or attempt unauthorised access; (d) use the Platform to infringe any third-party rights.</p>

              <h4>14. Disclaimer of Warranties</h4>
              <p>To the maximum extent permitted by law, the Platform and Services are provided "as is" and "as available". Powow disclaims all warranties, express or implied.</p>

              <h4>15. Limitation of Liability</h4>
              <p>To the maximum extent permitted by law, Powow will not be liable for any indirect, incidental, special, consequential, or exemplary damages. Powow's total aggregate liability will not exceed the total fees paid by you in the twelve (12) months preceding the event giving rise to the claim.</p>

              <h4>16. Indemnification</h4>
              <p>You agree to indemnify, defend, and hold harmless Powow from and against any claims arising out of your use of the Platform, your devices and integrations, or your breach of these Terms.</p>

              <h4>17. Suspension and Termination</h4>
              <p>Powow may suspend or terminate your access to the Platform if you breach these Terms, your access presents a risk, or Powow is required to do so by law.</p>

              <h4>18. Changes to the Platform and Terms</h4>
              <p>Powow may modify, update, suspend, or discontinue any part of the Platform at any time. Your continued use after updated Terms take effect constitutes acceptance.</p>

              <h4>19. Governing Law and Jurisdiction</h4>
              <p>These Terms are governed by the laws of New South Wales, Australia. Each party submits to the exclusive jurisdiction of the courts of New South Wales.</p>

              <h4>20. Export Control and Sanctions</h4>
              <p>You must comply with all applicable export control, trade sanctions, and import laws and regulations.</p>

              <h4>21. Force Majeure</h4>
              <p>Neither party will be liable for failure or delay resulting from events beyond its reasonable control.</p>

              <h4>22. Assignment</h4>
              <p>You may not assign your rights or obligations without the prior written consent of Powow.</p>

              <h4>23. Entire Agreement</h4>
              <p>These Terms constitute the entire agreement between the parties regarding the Platform.</p>

              <h4>24. Severability</h4>
              <p>If any provision is found invalid, the remaining provisions remain in full force and effect.</p>

              <h4>25. Waiver</h4>
              <p>Failure to require performance of any provision will not affect the right to require later performance.</p>

              <h4>26. Survival</h4>
              <p>Provisions which by their nature should survive termination will survive.</p>

              <h4>27. Notices</h4>
              <p>All notices must be provided in writing. Notices to Powow should be sent to: Powow VPP Pty Ltd, Email: legal@powow.com.au</p>
            </div>
            <div className="tnc-modal-footer">
              <button className="btn-primary" onClick={() => { setAgree(true); setShowTnC(false); }}>I Agree</button>
              <button className="btn-secondary" onClick={() => setShowTnC(false)} style={{ marginLeft: 12 }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
